import { makeCopy, type Copy } from "@cfg/brand-sdk";
import type { DesignTranslator } from "@cfg/design-sdk";
import { createContext, useContext, type ReactNode } from "react";

/**
 * Active language for everything under it: core/brand copy, the locale for
 * Intl formatting, and (optionally) translations of design-pack text. The
 * shell provides it; without a provider (design studio, tests) the UI reads
 * English defaults.
 */
export interface LocaleValue {
  /** BCP 47 tag, e.g. "en" or "es-MX". */
  locale: string;
  copy: Copy;
  /** Design text translator for `locale`; absent = designs show as authored. */
  designs?: DesignTranslator;
}

const fallback: LocaleValue = { locale: "en", copy: makeCopy({ copy: {}, locale: "en", i18n: {} }) };

const LocaleCtx = createContext<LocaleValue>(fallback);

/** Pass a memoised `value`: a new object re-renders every consumer (that is how a language switch applies). */
export function LocaleProvider({ value, children }: { value: LocaleValue; children: ReactNode }) {
  return <LocaleCtx.Provider value={value}>{children}</LocaleCtx.Provider>;
}

export function useLocale(): LocaleValue {
  return useContext(LocaleCtx);
}

/** The copy lookup for the active locale. */
export function useCopy(): Copy {
  return useContext(LocaleCtx).copy;
}
